import { memo, useEffect } from 'react'
import { Text, TouchableOpacity, View, StyleSheet } from 'react-native'
import { colorPalette, globalStyles } from '../../static/styles/global'
import Select from '../shared/form/Select'
// utils
import {
  convertTo12HourTime,
  convertTo24HourTime,
  daysOfWeek,
  mapSelectKey,
  scheduleTimeRange
} from '../../utils'
// i18n
import { en as formSelectEn } from '../../i18n/FormSelect'
import ScheduleTimePicker from './ScheduleTimePicker'
import { useState } from 'react'
import AddNote from './AddNote'
import SelectKeyValue from '../shared/form/SelectKeyValue'
// components
import CustomButton from '../shared/button/Button'
// global types
import {
  AvailabilityDays,
  AvailabilityStatus,
  FridaySchedule,
  GlobalSchedule,
  MondaySchedule,
  SaturdaySchedule,
  SundaySchedule,
  ThursdaySchedule,
  TimeFormat,
  TuesdaySchedule,
  WednesdaySchedule
} from '../../types/scheduleTypes'
//translation
import { useTranslation } from 'react-i18next'
interface StartEndTimeProps {
  startTime: string
  startTimePickerVisible: any
  setStartTimePickerVisible: any
  endTime: string
  endTimePickerVisible: any
  setEndTimePickerVisible: any
}
const INIT_TIME = '00:00 am'

const StartEndTime = ({
  startTime,
  startTimePickerVisible,
  setStartTimePickerVisible,
  endTime,
  endTimePickerVisible,
  setEndTimePickerVisible
}: StartEndTimeProps): JSX.Element => {
  const { t } = useTranslation()
  return (
    <View style={[globalStyles.hDirection, globalStyles.mt2, globalStyles.mb1]}>
      <View style={[globalStyles.ml4, globalStyles.mr8]}>
        <Text
          style={[
            globalStyles.text,
            globalStyles.grayTextColor,
            globalStyles.fs11,
            globalStyles.mb1
          ]}
        >
          {t('starttime')}
        </Text>
        <TouchableOpacity
          style={[false && styles.timeUnderline]}
          onPress={() => {
            setStartTimePickerVisible(!startTimePickerVisible)
          }}
          // disabled
        >
          <Text
            style={[
              globalStyles.text,
              globalStyles.blackTextColor,
              globalStyles.fs12,
              globalStyles.mb1,
              globalStyles.textLowecase
            ]}
          >
            {startTime ? startTime : INIT_TIME}
          </Text>
        </TouchableOpacity>
      </View>
      {/* start time */}
      <View>
        <Text
          style={[
            globalStyles.text,
            globalStyles.darkTextColor,
            globalStyles.fs11,
            globalStyles.mb1
          ]}
        >
          {t('endtime')}
        </Text>
        <TouchableOpacity
          style={[false && styles.timeUnderline]}
          onPress={() => {
            setEndTimePickerVisible(!endTimePickerVisible)
          }}
          // disabled
          disabled={!startTime}
        >
          <Text
            style={[
              globalStyles.text,
              globalStyles.blackTextColor,
              !startTime && false && globalStyles.lightGrayTextColor,
              globalStyles.fs12,
              globalStyles.mb1,
              globalStyles.textLowecase
            ]}
          >
            {endTime ? endTime : INIT_TIME}
          </Text>
        </TouchableOpacity>

        {startTime && !endTime && (
          <Text
            style={[
              globalStyles.text,
              globalStyles.dangerTextColor,
              globalStyles.fs11,
              globalStyles.mt1
            ]}
          >
            {t('selectendtime')}
          </Text>
        )}
      </View>
      {/* end time */}
    </View>
  )
}
import { Schedule } from '../../types/globalTypes'
type AvailabilityCardProps = {
  day: AvailabilityDays
  schedules: Schedule[]
  addSecondShift?: boolean
  setAddSecondShift?: (val: boolean) => void
}
// store
import { useScheduleStore } from '../../stores/schedule'
const AvailabilityCardReadOnly = memo(
  ({
    day,
    schedules,
    addSecondShift: propAddSecondShift,
    setAddSecondShift: propSetAddSecondShift
  }: AvailabilityCardProps): JSX.Element => {
    const [availability, setAvailability] = useState<AvailabilityStatus>('A')
    /***********************************************/
    /* LOCAL STATES
    /***********************************************/
    // note
    const [addNoteVisible, setAddNoteVisible] = useState(false)
    const [editNoteVisible, setEditNoteVisible] = useState(false)
    const [note, setNote] = useState(null)
    const [savedNote, setSavedNote] = useState(null)
    // first shift
    const [localAddSecondShift, setLocalAddSecondShift] = useState(false)
    const addSecondShift =
      propAddSecondShift !== undefined ? propAddSecondShift : localAddSecondShift
    const setAddSecondShift =
      propSetAddSecondShift !== undefined
        ? propSetAddSecondShift
        : setLocalAddSecondShift
    const [startTime, setStartTime] = useState(null)
    const [endTime, setEndTime] = useState(null)
    const [startTimePickerVisible, setStartTimePickerVisible] = useState(false)
    const [endTimePickerVisible, setEndTimePickerVisible] = useState(false)
    const [startTimeFrom, setStartTimeFrom] = useState(0)
    const [endTimeFrom, setEndTimeFrom] = useState(0)

    const [selectedStartTimeItem, setSelectedStartTimeItem] = useState(null)
    const [selectedEndTimeItem, setSelectedEdnTimeItem] = useState(null)
    // second shift
    const [startSecondTimeShift, setStartSecondTimeShift] = useState(null)
    const [endSecondTimeShift, setEndSecondTimeShift] = useState(null)
    const [
      startSecondTimeShiftPickerVisible,
      setStartSecondTimeShiftPickerVisible
    ] = useState(false)
    const [
      endSecondTimeShiftPickerVisible,
      setEndSecondTimeShiftPickerVisible
    ] = useState(false)
    const [startSecondTimeShiftFrom, setStartSecondTimeShiftFrom] = useState(0)
    const [endSecondTimeShiftFrom, setEndSecondTimeShiftFrom] = useState(0)

    const [selectedStartSecondTimeShiftItem, setStartSecondTimeShiftItem] =
      useState(null)
    const [selectedEndSecondTimeShiftItem, setSelectedEdnSecondTimeShiftItem] =
      useState(null)

    // current schedule
    const [currentSchedule, setCurrentSchedule] = useState<
      | MondaySchedule
      | TuesdaySchedule
      | WednesdaySchedule
      | ThursdaySchedule
      | FridaySchedule
      | SaturdaySchedule
      | SundaySchedule
      | null
    >(null)
    /***********************************************/
    /* STORE DATA
    /***********************************************/
    // schedule store
    const { getSchedule, setSchedule } = useScheduleStore()
    /***********************************************/
    /* START HANDLERS
    /***********************************************/
    // handle set schedule day
    const handleSelectAvailability = (option: AvailabilityStatus) => {
      setAvailability(option)
      if (option === 'A') {
        setSchedule(day, 'A', null, null, null, null, null)
      } else if (option === 'S') {
        setSchedule(day, 'S', null, null, null, null, null)
      } else if (option === 'N') {
        setSchedule(day, 'N', null, null, null, null, null)
      }
    }
    // handle get schedule day
    const handleGetScheduleDay = (day: number) => {
      console.clear()
      // console.log('getSchedule(day):', getSchedule(day))
    }
    // handle set schedule day
    const handleSetScheduleDay = (
      day: number,
      availability: AvailabilityStatus,
      note: string,
      startTime: TimeFormat,
      endTime: TimeFormat,
      startSecondTimeShift: TimeFormat,
      endSecondTimeShift: TimeFormat
    ) => {
      // console.clear()
      setSchedule(
        day,
        availability,
        note,
        startTime,
        endTime,
        startSecondTimeShift,
        endSecondTimeShift
      )
    }
    const handleSetTime = (
      sTime: string,
      eTime: string,
      sTime2: string,
      eTime2: string,
      time: number
    ) => {
      if (time === 1) {
        setStartTime(sTime)

        setEndTime(null)
        setStartSecondTimeShift(null)
        setEndSecondTimeShift(null)

        setSelectedEdnTimeItem(null)
        setStartSecondTimeShiftItem(null)
        setSelectedEdnSecondTimeShiftItem(null)
      }
      if (time === 2) {
        setEndTime(eTime)

        setStartSecondTimeShift(null)
        setEndSecondTimeShift(null)

        setStartSecondTimeShiftItem(null)
        setSelectedEdnSecondTimeShiftItem(null)
      }
      if (time === 3) {
        setStartSecondTimeShift(sTime2)

        setEndSecondTimeShift(null)

        setSelectedEdnSecondTimeShiftItem(null)
      }
      if (time === 4) {
        setEndSecondTimeShift(eTime2)
      }

      handleSetScheduleDay(
        day,
        availability,
        null,
        convertTo24HourTime(sTime),
        convertTo24HourTime(eTime),
        convertTo24HourTime(sTime2),
        convertTo24HourTime(eTime2)
      )
    }

    // handle set start time
    const handleSetStartTime = (time: string) => {
      setStartTime(time)
      handleSetScheduleDay(
        day,
        availability,
        null,
        convertTo24HourTime(time),
        convertTo24HourTime(endTime),
        convertTo24HourTime(startSecondTimeShift),
        convertTo24HourTime(endSecondTimeShift)
      )
    }
    // handle set end time
    const handleSetEndTime = (time: string) => {
      setEndTime(time)
      handleSetScheduleDay(
        day,
        availability,
        null,
        convertTo24HourTime(startTime),
        convertTo24HourTime(time),
        convertTo24HourTime(startSecondTimeShift),
        convertTo24HourTime(endSecondTimeShift)
      )
    }
    // handle set 2nd start time shift
    const handleSetStartSecondTimeShift = (time: string) => {
      setStartSecondTimeShift(time)
      handleSetScheduleDay(
        day,
        availability,
        null,
        convertTo24HourTime(startTime),
        convertTo24HourTime(endTime),
        convertTo24HourTime(time),
        convertTo24HourTime(endSecondTimeShift)
      )
    }
    // handle set 2nd end time shift
    const handleSetEndSecondTimeShift = (time: string) => {
      setEndSecondTimeShift(time)
      handleSetScheduleDay(
        day,
        availability,
        null,
        convertTo24HourTime(startTime),
        convertTo24HourTime(endTime),
        convertTo24HourTime(startSecondTimeShift),
        convertTo24HourTime(time)
      )
    }
    // handdle set add second shift
    const handdleSetAddSecondShift = (hasSecondShift: boolean) => {
      setAddSecondShift(hasSecondShift)
      setStartSecondTimeShift(null)
      setEndSecondTimeShift(null)
      setSelectedEdnSecondTimeShiftItem(null)
      setStartSecondTimeShiftItem(null)
      handleSetScheduleDay(
        day,
        availability,
        null,
        convertTo24HourTime(startTime),
        convertTo24HourTime(endTime),
        convertTo24HourTime(null),
        convertTo24HourTime(null)
      )
    }
    // handle save note
    const handleSaveNote = (note: string) => {
      handleSetScheduleDay(day, availability, note, null, null, null, null)
    }
    const selectedSchedule = (
      day: number,
      currentSchedule
      // | MondaySchedule
      // TuesdaySchedule
      // | WednesdaySchedule
      // ThursdaySchedule
      // FridaySchedule
      // SaturdaySchedule
      // | SundaySchedule
    ): GlobalSchedule | null => {
      let gSchedule = null
      if (day === 1) {
        gSchedule = {
          scheduleValue: currentSchedule?.mondayScheduleValue,
          scheduleNAComment: currentSchedule?.mondayScheduleNAComment,
          scheduleTime1Start: currentSchedule?.mondayScheduleTime1Start,
          scheduleTime1End: currentSchedule?.mondayScheduleTime1End,
          scheduleTime2Start: currentSchedule?.mondayScheduleTime2Start,
          scheduleTime2End: currentSchedule?.mondayScheduleTime2End
        }
      } else if (day === 2) {
        gSchedule = {
          scheduleValue: currentSchedule?.tuesdayScheduleValue,
          scheduleNAComment: currentSchedule?.tuesdayScheduleNAComment,
          scheduleTime1Start: currentSchedule?.tuesdayScheduleTime1Start,
          scheduleTime1End: currentSchedule?.tuesdayScheduleTime1End,
          scheduleTime2Start: currentSchedule?.tuesdayScheduleTime2Start,
          scheduleTime2End: currentSchedule?.tuesdayScheduleTime2End
        }
      } else if (day === 3) {
        gSchedule = {
          scheduleValue: currentSchedule?.wednesdayScheduleValue,
          scheduleNAComment: currentSchedule?.wednesdayScheduleNAComment,
          scheduleTime1Start: currentSchedule?.wednesdayScheduleTime1Start,
          scheduleTime1End: currentSchedule?.wednesdayScheduleTime1End,
          scheduleTime2Start: currentSchedule?.wednesdayScheduleTime2Start,
          scheduleTime2End: currentSchedule?.wednesdayScheduleTime2End
        }
      } else if (day === 4) {
        gSchedule = {
          scheduleValue: currentSchedule?.thursdayScheduleValue,
          scheduleNAComment: currentSchedule?.thursdayScheduleNAComment,
          scheduleTime1Start: currentSchedule?.thursdayScheduleTime1Start,
          scheduleTime1End: currentSchedule?.thursdayScheduleTime1End,
          scheduleTime2Start: currentSchedule?.thursdayScheduleTime2Start,
          scheduleTime2End: currentSchedule?.thursdayScheduleTime2End
        }
      } else if (day === 5) {
        gSchedule = {
          scheduleValue: currentSchedule?.fridayScheduleValue,
          scheduleNAComment: currentSchedule?.fridayScheduleNAComment,
          scheduleTime1Start: currentSchedule?.fridayScheduleTime1Start,
          scheduleTime1End: currentSchedule?.fridayScheduleTime1End,
          scheduleTime2Start: currentSchedule?.fridayScheduleTime2Start,
          scheduleTime2End: currentSchedule?.fridayScheduleTime2End
        }
      } else if (day === 6) {
        gSchedule = {
          scheduleValue: currentSchedule?.saturdayScheduleValue,
          scheduleNAComment: currentSchedule?.saturdayScheduleNAComment,
          scheduleTime1Start: currentSchedule?.saturdayScheduleTime1Start,
          scheduleTime1End: currentSchedule?.saturdayScheduleTime1End,
          scheduleTime2Start: currentSchedule?.saturdayScheduleTime2Start,
          scheduleTime2End: currentSchedule?.saturdayScheduleTime2End
        }
      } else if (day === 0) {
        gSchedule = {
          scheduleValue: currentSchedule?.sundayScheduleValue,
          scheduleNAComment: currentSchedule?.sundayScheduleNAComment,
          scheduleTime1Start: currentSchedule?.sundayScheduleTime1Start,
          scheduleTime1End: currentSchedule?.sundayScheduleTime1End,
          scheduleTime2Start: currentSchedule?.sundayScheduleTime2Start,
          scheduleTime2End: currentSchedule?.sundayScheduleTime2End
        }
      }
      return gSchedule
    }
    // console.log('*** selectedSchedule ***', selectedSchedule(1, getSchedule(1)))
    // console.log('*** currentSchedule ***', currentSchedule)
    /***********************************************/
    /* SIDE EFFECTS
    /***********************************************/
    useEffect(() => {
      setNote('')
      // reset scheculing
      // setSchedule(day, 'A', null, null, null, null, null)
    }, [])
    useEffect(() => {
      // if (availability === 'A') {
      //   setSchedule(day, 'A', null, null, null, null, null)
      // } else if (availability === 'S') {
      //   setSchedule(day, 'S', null, null, null, null, null)
      // } else if (availability === 'N') {
      //   setSchedule(day, 'N', null, null, null, null, null)
      // }
      setCurrentSchedule(getSchedule(day))
      // console.log('availability', availability)
      return () => {}
    }, [availability])
    // console.log('currentSchedule:', selectedSchedule(day, currentSchedule))
    // selectedSchedule(day, currentSchedule).scheduleValue
    // console.log('### get schedule ###', getSchedule(1))
    // console.log('### get schedule ###', getSchedule(2))
    // console.log('### get schedule ###', getSchedule(3))
    // console.log('### get schedule ###', getSchedule(4))
    // console.log('### get schedule ###', getSchedule(5))
    // console.log('### get schedule ###', getSchedule(6))
    // console.log('### get schedule ###', getSchedule(0))
    const { t } = useTranslation()
    const dayss = daysOfWeek()

    const schedulestore = useScheduleStore()

    useEffect(() => {
      let initialHasSecondShift = false
      if (day === 1) {
        setAvailability(schedulestore.scheduleData?.mondayScheduleValue)

        setStartTime(schedulestore.scheduleData?.mondayScheduleTime1Start)

        setEndTime(schedulestore.scheduleData?.mondayScheduleTime1End)
        setStartSecondTimeShift(
          schedulestore.scheduleData?.mondayScheduleTime2Start
        )
        setEndSecondTimeShift(
          schedulestore.scheduleData?.mondayScheduleTime2End
        )
        initialHasSecondShift = !!schedulestore.scheduleData?.mondayScheduleTime2Start
      } else if (day === 2) {
        setAvailability(schedulestore.scheduleData?.tuesdayScheduleValue)

        setStartTime(schedulestore.scheduleData?.tuesdayScheduleTime1Start)

        setEndTime(schedulestore.scheduleData?.tuesdayScheduleTime1End)
        setStartSecondTimeShift(
          schedulestore.scheduleData?.tuesdayScheduleTime2Start
        )
        setEndSecondTimeShift(
          schedulestore.scheduleData?.tuesdayScheduleTime2End
        )
        initialHasSecondShift = !!schedulestore.scheduleData?.tuesdayScheduleTime2Start
      } else if (day === 3) {
        setAvailability(schedulestore.scheduleData?.wednesdayScheduleValue)

        setStartTime(schedulestore.scheduleData?.wednesdayScheduleTime1Start)

        setEndTime(schedulestore.scheduleData?.wednesdayScheduleTime1End)
        setStartSecondTimeShift(
          schedulestore.scheduleData?.wednesdayScheduleTime2Start
        )
        setEndSecondTimeShift(
          schedulestore.scheduleData?.wednesdayScheduleTime2End
        )
        initialHasSecondShift = !!schedulestore.scheduleData?.wednesdayScheduleTime2Start
      } else if (day === 4) {
        setAvailability(schedulestore.scheduleData?.thursdayScheduleValue)

        setStartTime(schedulestore.scheduleData?.thursdayScheduleTime1Start)

        setEndTime(schedulestore.scheduleData?.thursdayScheduleTime1End)
        setStartSecondTimeShift(
          schedulestore.scheduleData?.thursdayScheduleTime2Start
        )
        setEndSecondTimeShift(
          schedulestore.scheduleData?.thursdayScheduleTime2End
        )
        initialHasSecondShift = !!schedulestore.scheduleData?.thursdayScheduleTime2Start
      } else if (day === 5) {
        setAvailability(schedulestore.scheduleData?.fridayScheduleValue)

        setStartTime(schedulestore.scheduleData?.fridayScheduleTime1Start)

        setEndTime(schedulestore.scheduleData?.fridayScheduleTime1End)
        setStartSecondTimeShift(
          schedulestore.scheduleData?.fridayScheduleTime2Start
        )
        setEndSecondTimeShift(
          schedulestore.scheduleData?.fridayScheduleTime2End
        )
        initialHasSecondShift = !!schedulestore.scheduleData?.fridayScheduleTime2Start
      } else if (day === 6) {
        setAvailability(schedulestore.scheduleData?.saturdayScheduleValue)
        setStartTime(schedulestore.scheduleData?.saturdayScheduleTime1Start)

        setEndTime(schedulestore.scheduleData?.saturdayScheduleTime1End)
        setStartSecondTimeShift(
          schedulestore.scheduleData?.saturdayScheduleTime2Start
        )
        setEndSecondTimeShift(
          schedulestore.scheduleData?.saturdayScheduleTime2End
        )
        initialHasSecondShift = !!schedulestore.scheduleData?.saturdayScheduleTime2Start
      } else if (day === 0) {
        setAvailability(schedulestore.scheduleData?.sundayScheduleValue)

        setStartTime(schedulestore.scheduleData?.sundayScheduleTime1Start)

        setEndTime(schedulestore.scheduleData?.sundayScheduleTime1End)
        setStartSecondTimeShift(
          schedulestore.scheduleData?.sundayScheduleTime2Start
        )
        setEndSecondTimeShift(
          schedulestore.scheduleData?.sundayScheduleTime2End
        )
        initialHasSecondShift = !!schedulestore.scheduleData?.sundayScheduleTime2Start
      }
      setAddSecondShift(initialHasSecondShift)
    }, [])

    const findIdForTimeString = (timeString) => {
      if (!timeString) {
        return 0
      }

      const splitTimeString = timeString.split(' ')

      if (splitTimeString.length !== 2) {
        console.log('Invalid timeString format')
        return 0
      }

      const [time, period] = splitTimeString
      const lowercaseperiod = period.toLowerCase()

      const splitTime = time.split(':')

      if (splitTime.length !== 2) {
        return 0
      }

      const [hour, minute] = splitTime.map(Number)

      let hour24 = hour

      for (let i = 0; i < scheduleTimeRange.length; i++) {
        const time = scheduleTimeRange[i]
        if (
          time.hour === hour24 &&
          time.minute === minute &&
          time.period === lowercaseperiod
        ) {
          return time.id
        }
      }

      return 0
    }

    return (
      <View>
        {currentSchedule && (
          <>
            <View
              style={[
                globalStyles.hDirection,
                globalStyles.hBetweenItems,
                globalStyles.vCenterItems
              ]}
            >
              <Text
                style={[
                  globalStyles.text,
                  globalStyles.fwBold,
                  globalStyles.fs18,
                  globalStyles.primaryTextColor,
                  globalStyles.flexOne
                ]}
              >
                {dayss[day]}
              </Text>
              <View>
                <SelectKeyValue
                  title={`${dayss[day]}` + t('availability')}
                  options={schedules}
                  selectedOption={mapSelectKey(
                    schedules,
                    selectedSchedule(day, getSchedule(day)).scheduleValue
                  )}
                  onSelect={(option: AvailabilityStatus): void => {
                    handleSelectAvailability(option)
                  }}
                  status={{}}
                  textColor={'secondary'}
                  // isDisabled
                />
              </View>
            </View>
            {/* start select action */}
            {false && (
              <View>
                <CustomButton
                  text="Set Schedule"
                  onPress={handleSetScheduleDay}
                  reverse
                  transparent
                />
                <CustomButton
                  text="Get Schedule"
                  onPress={() => handleGetScheduleDay(day)}
                  reverse
                  transparent
                />
              </View>
            )}
            {/* end select action */}
            {selectedSchedule(day, getSchedule(day)).scheduleValue ===
              schedules[1]['scheduleValue'] && (
              <>
                <View
                  style={[
                    globalStyles.hDirection,
                    globalStyles.hBetweenItems,
                    globalStyles.vStartItems,
                    globalStyles.mt1
                  ]}
                >
                  <StartEndTime
                    startTime={convertTo12HourTime(
                      selectedSchedule(day, getSchedule(day))
                        ?.scheduleTime1Start
                    )}
                    startTimePickerVisible={startTimePickerVisible}
                    setStartTimePickerVisible={setStartTimePickerVisible}
                    endTime={convertTo12HourTime(
                      selectedSchedule(day, getSchedule(day))?.scheduleTime1End
                    )}
                    endTimePickerVisible={endTimePickerVisible}
                    setEndTimePickerVisible={setEndTimePickerVisible}
                  />

                  {!addSecondShift && (
                    <View>
                      <TouchableOpacity
                        onPress={() => {
                          setAddSecondShift(!addSecondShift)
                          setStartSecondTimeShift(null)
                          setEndSecondTimeShift(null)
                        }}
                      >
                        <Text
                          style={[
                            globalStyles.text,
                            globalStyles.fs12,
                            globalStyles.secondaryTextColor,
                            globalStyles.mt6
                          ]}
                        >
                          +{t('addsecondshift')}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  )}
                  {/* add second shift OR remove second shift */}
                </View>
                <ScheduleTimePicker
                  title={t('selectstarttime')}
                  visible={startTimePickerVisible}
                  setVisible={setStartTimePickerVisible}
                  // onSelect={(time: string) => {
                  //   handleSetStartTime(time)
                  // }}
                  onSelect={(time: string) => {
                    handleSetStartTime(time)
                    handleSetTime(time, null, null, null, 1)
                  }}
                  setPickTimeFrom={setEndTimeFrom}
                  selectedItem={selectedStartTimeItem}
                  setSelectedItem={setSelectedStartTimeItem}
                />
                <ScheduleTimePicker
                  title={t('selectendtime')}
                  visible={endTimePickerVisible}
                  setVisible={setEndTimePickerVisible}
                  // onSelect={(time: string) => {
                  //   handleSetEndTime(time)
                  // }}
                  onSelect={(time: string) => {
                    handleSetTime(startTime, time, null, null, 2)
                    handleSetEndTime(time)
                  }}
                  pickTimeFrom={
                    endTimeFrom == 0
                      ? findIdForTimeString(convertTo12HourTime(startTime))
                      : endTimeFrom
                  }
                  setPickTimeFrom={setStartSecondTimeShiftFrom}
                  selectedItem={selectedEndTimeItem}
                  setSelectedItem={setSelectedEdnTimeItem}
                />

                {addSecondShift && (
                  <>
                    <View style={[globalStyles.mt2]} />
                    <View
                      style={[
                        globalStyles.hDirection,
                        globalStyles.hBetweenItems,
                        globalStyles.vStartItems,
                        globalStyles.mt1
                      ]}
                    >
                      <StartEndTime
                        startTime={convertTo12HourTime(
                          selectedSchedule(day, getSchedule(day))
                            ?.scheduleTime2Start
                        )}
                        startTimePickerVisible={
                          startSecondTimeShiftPickerVisible
                        }
                        setStartTimePickerVisible={
                          setStartSecondTimeShiftPickerVisible
                        }
                        endTime={convertTo12HourTime(
                          selectedSchedule(day, getSchedule(day))
                            ?.scheduleTime2End
                        )}
                        endTimePickerVisible={endSecondTimeShiftPickerVisible}
                        setEndTimePickerVisible={
                          setEndSecondTimeShiftPickerVisible
                        }
                      />

                      <View>
                        <TouchableOpacity
                          onPress={() => {
                            handdleSetAddSecondShift(!addSecondShift)
                          }}
                        >
                          <Text
                            style={[
                              globalStyles.text,
                              globalStyles.fs12,
                              globalStyles.dangerTextColor,
                              globalStyles.mt6
                            ]}
                          >
                            x{t('remove')}
                          </Text>
                        </TouchableOpacity>
                      </View>
                      {/* add second shift OR remove second shift */}
                    </View>

                    <ScheduleTimePicker
                      title={t('selectstarttime')}
                      visible={startSecondTimeShiftPickerVisible}
                      setVisible={setStartSecondTimeShiftPickerVisible}
                      onSelect={(time: string) => {
                        handleSetStartSecondTimeShift(time)
                        handleSetTime(startTime, endTime, time, null, 3)
                      }}
                      pickTimeFrom={
                        startSecondTimeShiftFrom == 0
                          ? findIdForTimeString(convertTo12HourTime(endTime))
                          : startSecondTimeShiftFrom
                      }
                      setPickTimeFrom={setEndSecondTimeShiftFrom}
                      selectedItem={selectedStartSecondTimeShiftItem}
                      setSelectedItem={setStartSecondTimeShiftItem}
                    />
                    <ScheduleTimePicker
                      title={t('selectendtime')}
                      visible={endSecondTimeShiftPickerVisible}
                      setVisible={setEndSecondTimeShiftPickerVisible}
                      onSelect={(time: string) => {
                        handleSetEndSecondTimeShift(time)
                        handleSetTime(
                          startTime,
                          endTime,
                          startSecondTimeShift,
                          time,
                          4
                        )
                      }}
                      pickTimeFrom={
                        endSecondTimeShiftFrom == 0
                          ? findIdForTimeString(
                              convertTo12HourTime(startSecondTimeShift)
                            )
                          : endSecondTimeShiftFrom
                      }
                      selectedItem={selectedEndSecondTimeShiftItem}
                      setSelectedItem={setSelectedEdnSecondTimeShiftItem}
                    />
                  </>
                )}
              </>
            )}
            {/* select availability times */}
            {selectedSchedule(day, getSchedule(day)).scheduleValue ===
              schedules[2]['scheduleValue'] && (
              <>
                {
                  <View
                    style={[
                      globalStyles.hDirection,
                      globalStyles.hBetweenItems,
                      globalStyles.vStartItems,
                      globalStyles.mt4
                    ]}
                  >
                    <View style={[globalStyles.flexOne]}>
                      {(savedNote || true) && (
                        <Text
                          style={[
                            globalStyles.text,
                            globalStyles.fs12,
                            globalStyles.grayTextColor,
                            globalStyles.flexOne,
                            globalStyles.pr4
                          ]}
                        >
                          {t('note')}{' '}
                          {
                            selectedSchedule(day, getSchedule(day))
                              ?.scheduleNAComment
                          }
                        </Text>
                      )}
                    </View>
                    {!savedNote && (
                      <TouchableOpacity
                        onPress={() => {
                          {
                            setAddNoteVisible(!addNoteVisible)
                          }
                        }}
                      >
                        <Text
                          style={[
                            globalStyles.text,
                            globalStyles.fs12,
                            globalStyles.secondaryTextColor
                          ]}
                        >
                          +{t('addnote')}
                        </Text>
                      </TouchableOpacity>
                    )}
                    {savedNote && (
                      <TouchableOpacity
                        onPress={() => {
                          {
                            setEditNoteVisible(!editNoteVisible)
                          }
                        }}
                      >
                        <Text
                          style={[
                            globalStyles.text,
                            globalStyles.fs12,
                            globalStyles.secondaryTextColor
                          ]}
                        >
                          {t('editnote')}
                        </Text>
                      </TouchableOpacity>
                    )}
                  </View>
                }
                <AddNote
                  title={t('addnote')}
                  visible={addNoteVisible}
                  setVisible={setAddNoteVisible}
                  note={note}
                  setNote={setNote}
                  savedNote={savedNote}
                  setSavedNote={setSavedNote}
                  handleSaveNote={handleSaveNote}
                  mode="add"
                />
                <AddNote
                  title={t('editnote')}
                  visible={editNoteVisible}
                  setVisible={setEditNoteVisible}
                  note={note}
                  setNote={setNote}
                  savedNote={savedNote}
                  setSavedNote={setSavedNote}
                  handleSaveNote={handleSaveNote}
                  mode="edit"
                />
              </>
            )}
            {/* not available */}
          </>
        )}
      </View>
    )
  }
)
const styles = StyleSheet.create({
  timeUnderline: {
    alignSelf: 'flex-start',
    borderBottomColor: colorPalette.secondary,
    borderBottomWidth: 1
  }
})
export default AvailabilityCardReadOnly
